import { Box, PropertyCard } from '@zillow/constellation-10';

import { LoadingParagraph, LoadingTag, LoadingText } from '@zg-rentals/loading-components';

import numberUtils from '../../utils/numberUtils';
import propertyUtils from '../../utils/propertyUtils';
import { getTagTypeByKey, STATUS_KEY } from '../../utils/statusTagUtils';
import stringHelper from '../../utils/stringHelper';

import {
  cityStateStyles,
  containerStyles,
  contentContainerStyles,
  emailTextStyles,
  feedSourceStyles,
  flexStyles,
  phoneTextStyles,
  photoContainerStyles,
  streetTextStyles,
} from './styles';

import type {
  UserClaimGetExclusivePropertiesForAllUnverifiedListingDto,
  UserClaimGetExclusivePropertiesListingDto,
} from '@zg-rentals/ts-rental-manager-api-client';

const { PROPERTY_DETAILS_OVERVIEW } = STATUS_KEY;

type ClaimListingCardProps = {
  listingData: UserClaimGetExclusivePropertiesForAllUnverifiedListingDto | UserClaimGetExclusivePropertiesListingDto;
  highlightPhone?: boolean;
  highlightEmail?: boolean;
  loading?: boolean;
};

const ClaimListingCard = ({
  listingData: { address, photoUrl, feedName, status, contactEmail, contactPhone },
  highlightPhone = false,
  highlightEmail = false,
  loading = false,
}: ClaimListingCardProps) => {
  const { formattedStreet, formattedCityStateZip } = propertyUtils.formatAddress(address);
  const formattedPhone = numberUtils.formatPhoneNumberInput(contactPhone);

  return (
    <Box data-testid="ClaimListingCard" css={containerStyles}>
      <Box css={contentContainerStyles}>
        <Box css={{ display: 'flex', alignItems: 'center' }}>
          <Box
            css={{ position: 'relative', overflow: 'hidden', ...photoContainerStyles }}
            data-loading={loading || null}
          >
            {!loading && (
              <PropertyCard.Photo
                src={photoUrl ?? undefined}
                alt="property"
                fallbackImg={<PropertyCard.PhotoFallback />}
                css={{ width: '100%', height: '100%' }}
              />
            )}
            <Box
              css={{
                position: 'absolute',
                top: 0,
                left: 0,
                paddingLeft: '200',
                paddingTop: '200',
                lg: { display: 'none' },
              }}
            >
              <LoadingTag
                tone={loading ? undefined : getTagTypeByKey(PROPERTY_DETAILS_OVERVIEW, status)}
                size="sm"
                isLoading={loading}
                loadingWidth="100px"
              >
                {stringHelper.sentenceCase(status)}
              </LoadingTag>
            </Box>
          </Box>
        </Box>
        <Box css={flexStyles}>
          <Box css={{ display: { base: 'none', lg: 'block' }, marginBlock: '200' }}>
            <LoadingTag
              tone={loading ? undefined : getTagTypeByKey(PROPERTY_DETAILS_OVERVIEW, status)}
              size="sm"
              isLoading={loading}
              loadingWidth="100px"
            >
              {stringHelper.sentenceCase(status)}
            </LoadingTag>
          </Box>
          <LoadingParagraph css={streetTextStyles} isLoading={loading} loadingWidth="100%">
            {formattedStreet}
          </LoadingParagraph>
          <LoadingParagraph css={cityStateStyles} isLoading={loading} loadingWidth="100%">
            {formattedCityStateZip}
          </LoadingParagraph>
          <Box css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' }, margin: '{spacing.100} 0' }}>
            {feedName && (
              <LoadingText css={feedSourceStyles} isLoading={loading} loadingWidth="100%">
                {feedName}
              </LoadingText>
            )}
            {contactEmail && (
              <LoadingText
                css={emailTextStyles.raw({ highlight: highlightEmail })}
                className="fs-mask"
                isLoading={loading}
                loadingWidth="100%"
              >
                {contactEmail}
              </LoadingText>
            )}
            {formattedPhone && (
              <LoadingText
                css={phoneTextStyles.raw({ highlight: highlightPhone })}
                className="fs-mask"
                isLoading={loading}
                loadingWidth="100%"
              >
                {formattedPhone}
              </LoadingText>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ClaimListingCard;
